import React, { useMemo } from "react";
import { ActionToolConfig, ConfigResponse } from "@/generated";
import { MissingIntegrations } from "../Spotflow/MissingIntegrations";
import { Skeleton } from "../shadcn/skeleton";
import { useForm } from "react-hook-form";
import { ToolFormRenderer } from "../Form/ToolFormRenderer";
import { NoxusFormEntryConfig } from "../Spotflow/ConfigDrawer/ConfigDrawer";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { useEditorReadOnly } from "@/hooks/useEditorReadOnly";

interface ActionConfigurationStepProps {
  nodeName: string;
  toolConfig: ActionToolConfig | null;
  onToolConfigChange: (config: ActionToolConfig) => void;
  configResponse?: ConfigResponse;
  isLoadingConfig: boolean;
  integrations?: Array<string | string[]>;
}

export const ActionConfigurationStep = ({
  nodeName,
  toolConfig,
  onToolConfigChange,
  configResponse,
  isLoadingConfig,
  integrations,
}: ActionConfigurationStepProps) => {
  const { spotGroup } = useSpotUser();
  const { hasPermission } = usePermissions();
  const isReadOnly = useEditorReadOnly();

  const defaultFormValues = useMemo(
    () => ({
      ...toolConfig?.config_overrides,
      ...toolConfig?.input_overrides,
    }),
    [toolConfig]
  );

  const form = useForm({
    defaultValues: defaultFormValues,
  });

  const { data: integrationsData } = useRemoteData(
    DATA_KEYS.INTEGRATIONS,
    { path: { group_id: spotGroup.id } },
    { enabled: !isReadOnly }
  );

  const defaultToolConfig: ActionToolConfig = useMemo(
    () =>
      toolConfig || {
        config_overrides: {},
        input_overrides: {},
      },
    [toolConfig]
  );

  const missingIntegrations = useMemo(() => {
    if (!integrationsData || !integrations) return [];
    const activeIntegrations = Object.keys(integrationsData).filter(
      (key) => integrationsData[key as keyof typeof integrationsData]
    );

    return (integrations as Array<string | string[]>).filter((requirement) => {
      if (Array.isArray(requirement)) {
        return requirement.every((i) => !activeIntegrations.includes(i));
      }
      return !activeIntegrations.includes(requirement);
    });
  }, [integrationsData, integrations]);

  if (isLoadingConfig) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (missingIntegrations.length > 0) {
    return (
      <MissingIntegrations
        missingIntegrations={missingIntegrations}
        canEditIntegrations={hasPermission(WORKSPACE_PERMISSIONS.EDIT, "group")}
        bannerType="action"
      />
    );
  }

  return (
    <ToolFormRenderer
      config={
        configResponse?.config as
          | Record<string, NoxusFormEntryConfig>
          | undefined
      }
      inputs={configResponse?.inputs}
      form={form}
      toolConfig={defaultToolConfig}
      onToolConfigChange={onToolConfigChange}
    />
  );
};
